import { AxiosError, CanceledError } from 'axios';

import { createDemoDataset } from './dataset';
import { createDemoServer } from './server';

const DEFAULT_LATENCY = [160, 420];

function wait(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new CanceledError());
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener?.('abort', handleAbort);
            resolve();
        }, ms);
        function handleAbort() {
            clearTimeout(timer);
            reject(new CanceledError());
        }
        signal?.addEventListener?.('abort', handleAbort, { once: true });
    });
}

function parseBody(data) {
    if (data == null || data === '') return undefined;
    if (typeof data !== 'string') return data;
    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}

// Axios adapter that answers from the in-memory demo server. Plugging it into
// the same `api` instance keeps interceptors, ApiError and TanStack Query on
// the real code path; only the transport differs. No request leaves the device.
export function createDemoAdapter({ latency = DEFAULT_LATENCY, now = () => Date.now(), dataset } = {}) {
    const server = createDemoServer(dataset || createDemoDataset(now()), { now });

    return async function demoAdapter(config) {
        const [min, max] = Array.isArray(latency) ? latency : [latency, latency];
        await wait(min + Math.random() * Math.max(0, max - min), config.signal);

        const method = String(config.method || 'get').toLowerCase();
        const { status, data } = server.handle(method, config.url, config.params, parseBody(config.data));
        const response = {
            data: structuredClone(data),
            status,
            statusText: String(status),
            headers: { 'x-demo': '1' },
            config,
            request: {},
        };
        if (status >= 400) {
            throw new AxiosError(data?.message || `Error ${status}`, AxiosError.ERR_BAD_REQUEST, config, {}, response);
        }
        return response;
    };
}
